/**
 * BrainHero — three cards side by side: Brain (lucide) + "AI" text + Fingerprint
 * (lucide), with subtle ArrowRight connectors between them. Concept:
 *   brain (biological potential) → AI (intelligent transformation) → fingerprint (data)
 *
 * Middle is text "AI" instead of an icon: user feedback found Cpu/Sparkles
 * ambiguous. Plain text label removes interpretation work — visual weight
 * still matches via font-size = ~0.58 × icon size.
 *
 * Library: lucide-react (MIT license, vector icons, no raster assets, no copyright
 * concerns).
 *
 * Motion preserved (user-approved):
 *   - Slow Y-axis float ±6px (4s reverse loop) on all 3 icons.
 *   - Soft breathing scale 1 ↔ 1.03 (6s reverse loop).
 *   - Pulsing brand-300 glow ring behind the whole composition (3s loop).
 *   - Stagger entrance: Brain in first → Cpu → Fingerprint, then 2 arrows fade in.
 *     Each icon: opacity 0→1, scale 0.8→1, with backOut easing.
 *   - prefers-reduced-motion nullifies all motion (content still renders).
 *
 * Brand integration:
 *   - Icons stroke lime brand-600 (#67911a) for visibility on light bg.
 *   - Small blue (#185FA5) core dot overlay at the center of Fingerprint — echoes
 *     the marker spec used throughout the app.
 */
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Brain, Fingerprint } from "lucide-react";

interface BrainHeroProps {
  /** Height/width of each main icon (px). Connector arrows scale proportionally. */
  size?: number;
  className?: string;
}

export function BrainHero({ size = 96, className = "" }: BrainHeroProps) {
  const reduce = useReducedMotion();

  const floatT = reduce
    ? { duration: 0 }
    : { duration: 4, ease: "easeInOut" as const, repeat: Infinity, repeatType: "reverse" as const };
  const breathT = reduce
    ? { duration: 0 }
    : { duration: 6, ease: "easeInOut" as const, repeat: Infinity, repeatType: "reverse" as const };

  // Stagger entrance — each icon gets a slightly later delay.
  function entranceProps(delay: number) {
    return {
      initial: { opacity: 0, scale: 0.8 },
      animate: { opacity: 1, scale: 1 },
      transition: reduce
        ? { duration: 0 }
        : { duration: 0.5, delay, ease: "backOut" as const },
    };
  }

  const arrowSize = Math.max(20, Math.round(size * 0.3));
  const stroke = "#67911a";    // brand-700
  const strokeHover = "#84b224"; // brand-500

  return (
    <div
      className={`relative inline-flex items-center justify-center gap-3 sm:gap-5 ${className}`}
      style={{ height: size + 16 }}
    >
      {/* Pulsing brand glow ring behind composition. */}
      {!reduce && (
        <span
          aria-hidden
          className="absolute -inset-6 rounded-[40%] bg-brand-300/30 blur-3xl animate-pulse-ring"
        />
      )}

      {/* === BRAIN === */}
      <motion.div
        className="relative flex items-center justify-center"
        animate={{ y: [0, -6, 0], scale: [1, 1.03, 1] }}
        transition={{ ...floatT, ...breathT }}
      >
        <motion.div
          {...entranceProps(reduce ? 0 : 0.0)}
          className="rounded-2xl bg-white/80 backdrop-blur-sm border border-brand-200 p-3 shadow-depth-2"
        >
          <Brain size={size} color={stroke} strokeWidth={1.5} aria-label="Otak" />
        </motion.div>
      </motion.div>

      {/* arrow 1 */}
      <motion.div
        {...entranceProps(reduce ? 0 : 1.0)}
        className="relative"
        animate={{ y: [0, -4, 0] }}
        transition={floatT}
      >
        <ArrowRight size={arrowSize} color={strokeHover} strokeWidth={2} className="opacity-70" aria-hidden />
      </motion.div>

      {/* === AI — text label (lebih jelas dari ikon, satu kotak setara visual). === */}
      <motion.div
        className="relative flex items-center justify-center"
        animate={{ y: [0, -6, 0], scale: [1, 1.03, 1] }}
        // Slight phase offset so the three cards don't bob in lock-step — feels alive.
        transition={{ ...floatT, ...breathT, delay: reduce ? 0 : 0.4 }}
      >
        <motion.div
          {...entranceProps(reduce ? 0 : 0.3)}
          className="rounded-2xl bg-white/80 backdrop-blur-sm border border-brand-200 p-3 shadow-depth-2"
        >
          <div
            aria-label="AI"
            className="flex items-center justify-center font-extrabold tracking-tight leading-none select-none"
            style={{
              width: size,
              height: size,
              color: stroke,
              fontSize: Math.round(size * 0.58),
            }}
          >
            AI
          </div>
        </motion.div>
      </motion.div>

      {/* arrow 2 */}
      <motion.div
        {...entranceProps(reduce ? 0 : 1.15)}
        className="relative"
        animate={{ y: [0, -4, 0] }}
        transition={{ ...floatT, delay: reduce ? 0 : 0.2 }}
      >
        <ArrowRight size={arrowSize} color={strokeHover} strokeWidth={2} className="opacity-70" aria-hidden />
      </motion.div>

      {/* === FINGERPRINT (with blue core dot overlay) === */}
      <motion.div
        className="relative flex items-center justify-center"
        animate={{ y: [0, -6, 0], scale: [1, 1.03, 1] }}
        transition={{ ...floatT, ...breathT, delay: reduce ? 0 : 0.8 }}
      >
        <motion.div
          {...entranceProps(reduce ? 0 : 0.6)}
          className="rounded-2xl bg-white/80 backdrop-blur-sm border border-brand-200 p-3 shadow-depth-2 relative"
        >
          <Fingerprint size={size} color={stroke} strokeWidth={1.5} aria-label="Sidik Jari" />
          {/* Blue core dot — overlay at icon center. Echoes marker spec. */}
          <motion.span
            aria-hidden
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              duration: reduce ? 0 : 0.4,
              delay: reduce ? 0 : 1.4,
              ease: "backOut",
            }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <span
              className="block rounded-full"
              style={{
                width: Math.max(6, size * 0.085),
                height: Math.max(6, size * 0.085),
                backgroundColor: "#185FA5",
                boxShadow: "0 0 0 3px rgba(24, 95, 165, 0.18)",
              }}
            />
          </motion.span>
        </motion.div>
      </motion.div>
    </div>
  );
}
